import { Location, NavigateFunction } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import { createCustomerChat } from '../../services/customers';
import {
  cleanupFileName,
  getCleanedPhone,
  getInitials,
} from '../../utils/global/helper';
import { navigateToChat } from '../appointments';
import { getFullName } from '../global';
import { Appointment, LoadedUser } from '../../interfaces';
import { ROUTES } from '../../constants/global';

export const buildCustomerProfileUrl = (
  customerId: string,
  location: Pick<Location, 'pathname' | 'search'>,
) =>
  `${ROUTES.customers}/${customerId}?returnTo=${encodeURIComponent(
    `${location.pathname}${location.search}`,
  )}`;

export const getCustomerFileName = ({
  businessId,
  customerId,
  file,
}: {
  businessId: string;
  customerId: string;
  file: File;
}) => {
  const ext = file.type.split('/')[1];
  const fileName = file.name.split('.')[0];
  const newFileName = `${fileName}.${ext}`;
  return `businesses/${businessId}/customers/${customerId}/${cleanupFileName(
    newFileName,
  )}`;
};

export interface ClientDisplay {
  initials: string;
  name: string;
  meta: string;
}

export type ClientDisplayAppointment = Pick<
  Appointment,
  'startDateTime' | 'timezone' | 'employee'
>;

// Single source of truth for how a client reads in the redesigned client
// section — the checkout and composer redesigns both derive from this.
export function getClientDisplay(
  client: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
  },
  appointment?: ClientDisplayAppointment | null,
  // Result rows separate contact details with a comma, the selected card
  // with a middot.
  separator = ' · ',
): ClientDisplay {
  const first = client.firstName?.trim() ?? '';
  const last = client.lastName?.trim() ?? '';
  const name = getFullName({ firstName: first, lastName: last });
  const initials =
    getInitials({ firstName: first, lastName: last }).toUpperCase() || '?';

  // With an appointment attached the card echoes the booking; otherwise it
  // falls back to contact details.
  const parts: string[] = [];
  if (appointment?.startDateTime) {
    const start = new Date(appointment.startDateTime);
    const timezone =
      appointment.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      'UTC';
    parts.push(formatInTimeZone(start, timezone, 'MMMM do, h:mm a'));
    // The provider is an employee, so it follows employee naming: shops that
    // set a displayName expect it used instead of the legal first name.
    const { employee } = appointment;
    const provider = (employee?.displayName || employee?.firstName)?.trim();
    if (provider) parts.push(`with ${provider}`);
  } else {
    if (client.phoneNumber)
      parts.push(getCleanedPhone(`${client.phoneNumber}`));
    if (client.email) parts.push(client.email);
  }

  return { initials, name, meta: parts.join(separator) };
}

export const openCustomerChat = async ({
  user,
  openLoadingModal,
  closeLoadingModal,
  navigate,
  customerId,
}: {
  user: LoadedUser;
  openLoadingModal: ReturnType<typeof useLoadingModal>['openLoadingModal'];
  closeLoadingModal: ReturnType<typeof useLoadingModal>['closeLoadingModal'];
  navigate: NavigateFunction;
  customerId: string;
}) => {
  if (!user || !customerId) return;

  try {
    openLoadingModal({
      header: 'Opening chat...',
      message: 'Please wait while we open the chat',
    });

    const chatResponse = await createCustomerChat({
      customerId,
      businessId: user.business.id,
    });
    if (!chatResponse) return;
    navigateToChat(navigate, {
      conversation: chatResponse,
      appointmentId: null,
      generalRequestId: null,
    });
  } catch {
    // Empty
  } finally {
    closeLoadingModal();
  }
};
